import { LineChart } from "@octopusdeploy/design-system-components";
import type { InsightsReportResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import { useEffect } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { InsightsReportLayout } from "~/areas/insights/components/InsightsReportLayout/InsightsReportLayout";
import { ReportMetricsLayout } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import type { ReportMetricsPageProps } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import { useReportFilters } from "~/areas/insights/components/Reports/ReportMetricsLayout/useReportFilters";
import { getChartAxisDateFormatter } from "~/areas/insights/dataTransformation/dateHelpers";
import { getChartReadyInsightsData, getYAxisUnitOfTimeKeys } from "~/areas/insights/dataTransformation/getChartReadyInsightsData";
import { getEvenlySpacedTickArray, getYAxisDataMax } from "~/areas/insights/dataTransformation/getYAxisDataMax";
import { formatDurationInWords } from "~/areas/insights/dataTransformation/stringHelpers";
import { insightsCadenceLookup, insightsCadenceXAxisTickInterval } from "~/areas/insights/insightsCadence";
import InternalLink from "~/components/Navigation/InternalLink";
import { InsightsTooltip } from "../../ChartTooltips/InsightsTooltip/InsightsTooltip";
import { InsightsChartCard } from "../../InsightsChartCard";
import { TrendIndicator } from "../TrendIndicator/TrendIndicator";
import styles from "./styles.module.less";
type Props = ReportMetricsPageProps;
function ReportOverviewInner({ bffResponse, report, cadence, busy }: Props) {
    const dispatchAction = useAnalyticSimpleActionDispatch();
    useEffect(() => {
        dispatchAction("View Insights Report Overview");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const chartData = getChartReadyInsightsData(bffResponse.Series);
    const { LeadTimeKey, MeanTimeToRecoveryKey } = getYAxisUnitOfTimeKeys(chartData);
    const currentGranularity = insightsCadenceLookup[cadence].granularity;
    const dateFormatter = getChartAxisDateFormatter(currentGranularity);
    const commonChartProps = {
        xAxisDataKey: "StartOfInterval",
        xAxisType: "category",
        yAxisType: "number",
        xTickFormatter: dateFormatter,
        showLegend: false,
        xAxisTickInterval: insightsCadenceXAxisTickInterval[cadence],
    } as const;
    const frequencyYAxisMax = getYAxisDataMax(chartData, "NumberOfSuccessfulDeployments");
    const leadTimeYAxisMax = getYAxisDataMax(chartData, `LeadTime.mean.${LeadTimeKey}`);
    const timeToRecoveryYAxisMax = getYAxisDataMax(chartData, `MeanTimeToRecovery.mean.${MeanTimeToRecoveryKey}`);
    return (<div className={styles.content}>
            <h2 className={styles.groupHeading}>Tempo</h2>
            <div className={cn(styles.twoColumnGrid, styles.tempoGrid)}>
                <div>
                    <div className={styles.titleContainer}>
                        <InternalLink to={links.deploymentFrequencyInsightsPage.generateUrl({ spaceId: report.SpaceId, reportId: report.Id })}>
                            <h3 className={styles.title}>Deployment frequency</h3>
                        </InternalLink>
                        <TrendIndicator trend={bffResponse.DeploymentFrequency.OverallTrend} trendKey="DeploymentFrequency"/>
                    </div>
                    <InsightsChartCard>
                        <LineChart data={chartData} loading={Boolean(busy)} dataKey="NumberOfSuccessfulDeployments" yAxisLabel="No. of deployments" tooltip={<InsightsTooltip dataKey="NumberOfSuccessfulDeployments" valueFormatter={(v) => `${v.toLocaleString()} deployment${v === 1 ? "" : "s"}`} cadence={cadence} showSeries/>} yAxisDomain={[0, frequencyYAxisMax]} yAxisTickArray={getEvenlySpacedTickArray(frequencyYAxisMax)} {...commonChartProps}/>
                    </InsightsChartCard>
                </div>
                <div>
                    <div className={styles.titleContainer}>
                        <InternalLink to={links.deploymentLeadTimeInsightsPage.generateUrl({ spaceId: report.SpaceId, reportId: report.Id })}>
                            <h3 className={styles.title}>Deployment lead time</h3>
                        </InternalLink>
                        <TrendIndicator trend={bffResponse.LeadTime.OverallTrend} trendKey="LeadTime"/>
                    </div>
                    <InsightsChartCard>
                        <LineChart data={chartData} loading={Boolean(busy)} dataKey={`LeadTime.mean.${LeadTimeKey}`} yAxisLabel={`Avg. no. of ${LeadTimeKey.toLowerCase()}`} tooltip={<InsightsTooltip dataKey="LeadTime.mean.duration" valueFormatter={formatDurationInWords} cadence={cadence} showSeries/>} yAxisDomain={[0, leadTimeYAxisMax]} yAxisTickArray={getEvenlySpacedTickArray(leadTimeYAxisMax)} {...commonChartProps}/>
                    </InsightsChartCard>
                </div>
            </div>
            <h2 className={styles.groupHeading}>Stability</h2>
            <div className={styles.twoColumnGrid}>
                <div>
                    <div className={styles.titleContainer}>
                        <InternalLink to={links.failureRateInsightsPage.generateUrl({ spaceId: report.SpaceId, reportId: report.Id })}>
                            <h3 className={styles.title}>Deployment failure rate</h3>
                        </InternalLink>
                        <TrendIndicator trend={bffResponse.ChangeFailureRate.OverallTrend} trendKey="ChangeFailureRate"/>
                    </div>
                    <InsightsChartCard>
                        <LineChart data={chartData} loading={Boolean(busy)} dataKey="DeploymentFailureRate.ComputedFailureRate" yAxisLabel="Failure rate (%)" yAxisDomain={[0, 100]} yAxisTickArray={getEvenlySpacedTickArray(100)} tooltip={<InsightsTooltip dataKey="DeploymentFailureRate.ComputedFailureRate" valueFormatter={(v) => `${v}%`} cadence={cadence} showSeries/>} {...commonChartProps}/>
                    </InsightsChartCard>
                </div>
                <div>
                    <div className={styles.titleContainer}>
                        <InternalLink to={links.meanTimeToRecoveryInsightsPage.generateUrl({ spaceId: report.SpaceId, reportId: report.Id })}>
                            <h3 className={styles.title}>Mean time to recovery</h3>
                        </InternalLink>
                        <TrendIndicator trend={bffResponse.MeanTimeToRecovery.OverallTrend} trendKey="MeanTimeToRecovery"/>
                    </div>
                    <InsightsChartCard>
                        <LineChart data={chartData} loading={Boolean(busy)} dataKey={`MeanTimeToRecovery.mean.${MeanTimeToRecoveryKey}`} yAxisLabel={`Avg. no. of ${MeanTimeToRecoveryKey.toLowerCase()}`} tooltip={<InsightsTooltip dataKey="MeanTimeToRecovery.mean.duration" valueFormatter={formatDurationInWords} cadence={cadence} showSeries/>} yAxisDomain={[0, timeToRecoveryYAxisMax]} yAxisTickArray={getEvenlySpacedTickArray(timeToRecoveryYAxisMax)} {...commonChartProps}/>
                    </InsightsChartCard>
                </div>
            </div>
        </div>);
}
export function InsightsReportOverviewPage({ report }: {
    report: InsightsReportResource;
}) {
    const [reportFilter, setReportFilter] = useReportFilters();
    return (<InsightsReportLayout report={report} reportFilter={reportFilter}>
            <ReportMetricsLayout title="Overview" report={report} reportFilter={reportFilter} setReportFilter={setReportFilter}>
                {(props) => <ReportOverviewInner {...props}/>}
            </ReportMetricsLayout>
        </InsightsReportLayout>);
}
